// pages/OrderListPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import Table from '../UI/Table';
import Select from '../UI/Select';
import Button from '../UI/Button';
import Modal from '../UI/Modal';
import QRCodeBox from '../UI/QRCodeBox';
import actionLabels from '../../config/actionLabels';

const OrderListPage = () => {
    const { user, logout, hasRole, hasAnyRole } = useUser();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [editForm, setEditForm] = useState({
        quantity: '',
        priority: 'normal',
        deadline: ''
    });

    const canViewOrchestration = hasAnyRole(["director", "departmentHead", "unitChief", "chief"]);
    const canEditOrchestration = hasAnyRole(["director", "departmentHead", "unitChief" ,"chief"]);
    const canViewDispatching = hasAnyRole([ "masrter", "teamLead"]);
    const canViewCompletion = hasRole("worker");

    useEffect(() => {
        fetchOrders();
    }, [statusFilter]);

    const fetchOrders = async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/orders${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`;
            const res = await fetchWithAuth(url, {}, logout);
            const data = await res.json();
            
            // Фильтрация по роли пользователя
			
            const filteredOrders = data.filter(order => {
				//Показывает все заказы
                if (canViewOrchestration) return true;
				//Показывает заказы если пользователь отмечен в фазе
                if (canViewDispatching) {
                    return order.productSnapshot.route.some(phase => 
                        phase.phaseManager === user.user.id
                    );
                }
				//Показывает заказы, в которых есть шаги, в которых есть сотрудник
                if (canViewCompletion) {
                    return order.productSnapshot.route.some(phase =>
                        phase.steps.some(step =>
                            step.assignees.some(assignee => assignee.userId === user.user.id)
                        )
                    );
                }
                return false;
            });

            setOrders(filteredOrders);
        } catch (err) {
            setError('Ошибка загрузки заказов');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (orderId) => {
        if (window.confirm('Вы уверены, что хотите удалить этот заказ?')) {
            try {
                await fetchWithAuth(
                    `${process.env.REACT_APP_API_URL}/orders/${orderId}`,
                    { method: 'DELETE' },
                    logout
                );
                fetchOrders();
            } catch (err) {
                setError('Ошибка удаления заказа');
            }
        }
    };

    const handleEdit = (order) => {
        setSelectedOrder(order);
        setEditForm({
            quantity: order.quantity,
            priority: order.priority,
            deadline: order.deadline ? new Date(order.deadline).toISOString().split('T')[0] : ''
        });
        setShowEditModal(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/orders/${selectedOrder._id}`,
                {
                    method: 'PUT',
                    body: JSON.stringify(editForm),
                },
                logout
            );
            setShowEditModal(false);
            fetchOrders();
        } catch (err) {
            setError('Ошибка обновления заказа');
        }
    };

    const columns = [
        { 
            accessor: 'productSnapshot', 
            header: 'Продукт',
            render: (productSnapshot) => productSnapshot?.name || 'Нет данных'
        },
        { 
            accessor: 'variation', 
            header: 'Вариация',
            render: (variation) => variation?.value || 'Нет данных'
        },
        { 
            accessor: 'quantity', 
            header: 'Количество',
            render: (quantity) => quantity || 0
        },
        { 
            accessor: 'status', 
            header: 'Статус',
            render: (status) => (
                <span className={`px-2 py-1 rounded text-sm ${
                    status === 'done' ? 'bg-green-500' :
                    status === 'in_progress' ? 'bg-yellow-500' :
                    'bg-gray-500'
                }`}>
                    {actionLabels.status[status]}
                </span>
            )
        },
        { 
            accessor: 'priority', 
            header: 'Приоритет',
            render: (priority) => actionLabels.priority[priority] || priority
        },
        { 
            accessor: 'createdAt', 
            header: 'Дата создания',
            render: (date) => date ? new Date(date).toLocaleDateString() : 'Нет данных'
        },
        { 
            accessor: 'deadline', 
            header: 'Срок выполнения',
            render: (deadline) => deadline ? new Date(deadline).toLocaleDateString() : 'Нет данных'
        },
        {
            accessor: 'qrCode',
            header: 'QR код',
            render: (_, order) => (
                <QRCodeBox
                    qrCodePath={order.qrCodePath}
                    type="order"
                    objectId={order._id}
                    withActions
                />
            )
        },
        {
            accessor: 'actions',
            header: 'Действия',
            render: (_, order) => (
                <div className="flex gap-2">
                    <Button
                        onClick={() => navigate(`/orders/${order._id}`)}
                        variant="primary"
                    >
                        Подробнее
                    </Button>
                    {canEditOrchestration && order.status !== 'in_progress' && (
                        <>
                            <Button
                                onClick={() => handleEdit(order)}
                                variant="secondary"
                            >
                                Редактировать
                            </Button>
                            <Button
                                onClick={() => handleDelete(order._id)}
                                variant="danger"
                            >
                                Удалить
                            </Button>
                        </>
                    )}
                </div>
            )
        }
    ];

    const data = orders.map(order => ({
        ...order,
        qrCode: order.qrCodePath
    }));

    if (loading) return <div>Загрузка...</div>;
    if (error) return <div className="text-red-500">{error}</div>;

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-white">Заказы :</h1>
                {/* <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    options={[
                        { value: 'all', label: 'Все статусы' },
                        { value: 'planned', label: 'Запланирован' },
                        { value: 'in_progress', label: 'В работе' },
                        { value: 'done', label: 'Завершен' }
                    ]}
                /> */}
            </div>
            <Table
                columns={columns}
                data={data}
                onRowClick={(order) => navigate(`/orders/${order._id}`)}
            />

            <Modal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                title="Редактирование заказа"
            >
                <form onSubmit={handleEditSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-400">Количество</label>
                        <input
                            type="number"
                            value={editForm.quantity}
                            onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })}
                            className="mt-1 block w-full rounded-md bg-[#2D2D30] border border-[#3E3E42] text-white px-3 py-2"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-400">Приоритет</label>
                        <select
                            value={editForm.priority}
                            onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                            className="mt-1 block w-full rounded-md bg-[#2D2D30] border border-[#3E3E42] text-white px-3 py-2"
                        >
                            <option value="low">Низкий</option>
                            <option value="normal">Обычный</option>
                            <option value="high">Высокий</option>
                            <option value="urgent">Срочный</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-400">Срок выполнения</label>
                        <input
                            type="date"
                            value={editForm.deadline}
                            onChange={(e) => setEditForm({ ...editForm, deadline: e.target.value })}
                            className="mt-1 block w-full rounded-md bg-[#2D2D30] border border-[#3E3E42] text-white px-3 py-2"
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            onClick={() => setShowEditModal(false)}
                            variant="secondary"
                        >
                            Отмена
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                        >
                            Сохранить
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default OrderListPage;